import type { PropsWithoutRef, Ref, RefObject } from 'react';
import { forwardRef, useMemo, useState } from 'react';

import type { TestIdProp } from '@pega/cosmos-react-core';
import { Card, Flex, registerIcon, useBreakpoint, useTestIds } from '@pega/cosmos-react-core';
import * as pictureIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/picture.icon';

import {
  StyledOpenGraphAnchor,
  StyledOpenGraphCardContent,
  StyledOpenGraphDescription,
  StyledOpenGraphImage,
  StyledOpenGraphImageContainer,
  StyledOpenGraphText,
  StyledOpenGraphTitle
} from './OpenGraphPreview.styles';
import { getOpenGraphPreviewTestIds } from './OpenGraphPreview.test-ids';

export interface OpenGraphPreviewProps extends TestIdProp {
  data: OpenGraphData;
  containerRef?: RefObject<HTMLDivElement>;
}

type Prefix = 'b64' | 'og' | 'twitter';

type StripPrefix<T extends object> = {
  [K in keyof T as K extends `${Prefix}:${infer U extends string}` ? U : K]: T[K];
};

export interface OpenGraphData
  extends StripPrefix<{
    'og:title'?: string;
    'og:description'?: string;
    'og:image'?: string;
    'og:image_alt'?: string;
    'og:site_name'?: string;
    'og:url'?: string;
    'b64:encoded_image'?: string;
  }> {
  document_title?: string;
  meta_description?: string;
  href: string;
  error?: boolean;
}

registerIcon(pictureIcon);

const OpenGraphPreview = forwardRef(function OpenGraphCard(
  { data: openGraphData, containerRef, testId }: PropsWithoutRef<OpenGraphPreviewProps>,
  ref: Ref<HTMLAnchorElement>
) {
  const testIds = useTestIds(testId, getOpenGraphPreviewTestIds);
  const isMediumOrAbove = useBreakpoint('md', { breakpointRef: containerRef });

  const imageExists = openGraphData.encoded_image || openGraphData.image;
  const [imageBroken, setImageBroken] = useState(false);
  const [loading, setLoading] = useState(true);

  const openGraphUrl = (() => {
    if (openGraphData.error) return;
    if (openGraphData.url && URL.canParse(openGraphData.url, openGraphData.href)) {
      return new URL(openGraphData.url, openGraphData.href);
    }
    return new URL(openGraphData.href);
  })();

  const imageContent = useMemo(() => {
    return imageBroken || !imageExists ? undefined : (
      <StyledOpenGraphImage
        data-testid={testIds.image}
        src={openGraphData.encoded_image ?? openGraphData.image}
        alt={openGraphData.image_alt || ''}
        onError={() => {
          setImageBroken(true);
        }}
        onLoad={() => {
          setLoading(false);
          setImageBroken(false);
        }}
      />
    );
  }, [openGraphData?.image, openGraphData?.encoded_image, loading, imageBroken]);

  // If remote site only has a <title> and no Open Graph info, we assume they want to opt-out of preview.
  const onlyTitle = Object.values(openGraphData).length === 2 && !!openGraphData.document_title;

  return (
    openGraphUrl &&
    !onlyTitle &&
    !openGraphData.error && (
      <StyledOpenGraphAnchor
        data-testid={testIds.root}
        href={openGraphData.href}
        target='_blank'
        $loading={openGraphData.encoded_image ?? openGraphData.image ? loading : false}
        compact={isMediumOrAbove && !!containerRef}
        ref={ref}
      >
        <Card>
          <StyledOpenGraphCardContent data-testid={testIds.content} container>
            {imageContent && (
              <StyledOpenGraphImageContainer
                imageHref={openGraphData.encoded_image ?? openGraphData.image}
                container={{ alignItems: 'stretch' }}
              >
                {imageContent}
              </StyledOpenGraphImageContainer>
            )}
            <Flex
              container={{
                direction: 'column',
                justify: 'between',
                pad: 1,
                gap: 0.75
              }}
              item={{ grow: 1 }}
            >
              <Flex container={{ direction: 'column', gap: 0.25 }}>
                <StyledOpenGraphTitle>
                  {openGraphData.title ?? openGraphData.document_title}
                </StyledOpenGraphTitle>
                <StyledOpenGraphDescription>
                  {openGraphData.description ?? openGraphData.meta_description}
                </StyledOpenGraphDescription>
              </Flex>
              <Flex container>
                <StyledOpenGraphText variant='secondary'>
                  {openGraphData.site_name || openGraphUrl.hostname}
                </StyledOpenGraphText>
              </Flex>
            </Flex>
          </StyledOpenGraphCardContent>
        </Card>
      </StyledOpenGraphAnchor>
    )
  );
});

export default OpenGraphPreview;
